/**
 * Fatigue / sleep / cognition / mood cluster engine.
 *
 * Survivorship symptoms travel together: fatigue, sleep disturbance, memory
 * problems, and mood interact, and the functional consequence (work, driving,
 * daily activity) is usually what the person actually cares about. Isolated
 * per-symptom views miss that. This module reads the existing on-device tool
 * logs and returns the interaction, not four separate numbers.
 *
 * Pure functions; the caller supplies already-parsed log arrays so the same
 * engine works for on-device state and for account-saved snapshots.
 */

export type SleepLog = { date: string; bedtime: string; waketime: string; awakenings: number; ess: number };
export type ActivityLog = { date: string; minutes: number; fatigueBefore: number; fatigueAfter: number; sleepHours: number };
export type CognitionLog = { date: string; attention: number; memory: number; processing: number; language: number; workImpact: boolean };
export type DistressLog = { date: string; thermometer: number; phq2: [number, number]; gad2: [number, number]; problems: string[] };

export type ClusterInputs = {
  sleep: SleepLog[];
  activity: ActivityLog[];
  cognition: CognitionLog[];
  distress: DistressLog[];
};

export type Domain = {
  key: "fatigue" | "sleep" | "cognition" | "mood" | "function";
  title: string;
  status: "ok" | "watch" | "attention" | "no-data";
  value: string;
  plain: string;
  toolLink: string;
};

export type Interaction = { title: string; detail: string; action: string };

export type ClusterReport = {
  domains: Domain[];
  loggedCount: number;
  interactions: Interaction[];
  functionalImpact: string;
  escalation: string[];
  clinicianNotes: string[];
};

function hoursBetween(bed: string, wake: string): number {
  if (!bed || !wake) return 0;
  const [bh, bm] = bed.split(":").map(Number);
  const [wh, wm] = wake.split(":").map(Number);
  let mins = wh * 60 + wm - (bh * 60 + bm);
  if (mins <= 0) mins += 24 * 60;
  return Math.round((mins / 60) * 10) / 10;
}

const avg = (nums: number[]) => (nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null);

export function buildClusterReport(input: ClusterInputs): ClusterReport {
  const sleep = input.sleep.slice(0, 14);
  const activity = input.activity.slice(0, 14);
  const cognition = input.cognition.slice(0, 14);
  const distress = input.distress.slice(0, 14);

  const sleepHours = avg(sleep.map((s) => hoursBetween(s.bedtime, s.waketime)));
  const ess = avg(sleep.map((s) => s.ess));
  const fatigueAfter = avg(activity.map((a) => a.fatigueAfter));
  const weekMinutes = activity.slice(0, 7).reduce((s, a) => s + (a.minutes || 0), 0);
  const cogAvg = avg(cognition.map((c) => (c.attention + c.memory + c.processing + c.language) / 4));
  const workHits = cognition.filter((c) => c.workImpact).length;
  const therm = avg(distress.map((d) => d.thermometer));
  const phq = avg(distress.map((d) => (d.phq2?.[0] ?? 0) + (d.phq2?.[1] ?? 0)));

  const domains: Domain[] = [
    {
      key: "fatigue",
      title: "Fatigue",
      status: fatigueAfter == null ? "no-data" : fatigueAfter >= 7 ? "attention" : fatigueAfter >= 4 ? "watch" : "ok",
      value: fatigueAfter == null ? "Not logged" : `${fatigueAfter.toFixed(1)}/10 after activity`,
      plain:
        fatigueAfter == null
          ? "Log a few activity sessions to see whether exertion is being followed by disproportionate fatigue."
          : fatigueAfter >= 4
            ? "Fatigue after activity is high enough to be worth investigating rather than pushing through."
            : "Fatigue after activity is in a reasonable range.",
      toolLink: "/institute/activity",
    },
    {
      key: "sleep",
      title: "Sleep",
      status: sleepHours == null ? "no-data" : sleepHours < 6 || (ess ?? 0) >= 11 ? "attention" : sleepHours < 7 ? "watch" : "ok",
      value: sleepHours == null ? "Not logged" : `${sleepHours.toFixed(1)} h, sleepiness ${ess?.toFixed(0) ?? "n/a"}/24`,
      plain:
        sleepHours == null
          ? "Sleep is the most treatable member of this cluster, and the one most often skipped."
          : (ess ?? 0) >= 11
            ? "Daytime sleepiness at this level suggests a sleep disorder worth formally evaluating, not just poor habits."
            : sleepHours < 7
              ? "Short sleep is likely feeding the fatigue and memory findings below."
              : "Sleep duration looks adequate.",
      toolLink: "/institute/sleep",
    },
    {
      key: "cognition",
      title: "Cognition",
      status: cogAvg == null ? "no-data" : cogAvg >= 5 ? "attention" : cogAvg >= 3 ? "watch" : "ok",
      value: cogAvg == null ? "Not logged" : `${cogAvg.toFixed(1)}/10 severity`,
      plain:
        cogAvg == null
          ? "Rate attention, memory, mental speed, and word-finding to see whether they track with sleep and mood."
          : cogAvg >= 3
            ? "Cognitive symptoms at this level are worth a formal look, especially if sleep and mood are also affected."
            : "Cognitive symptoms are mild in your recent entries.",
      toolLink: "/institute/cognition",
    },
    {
      key: "mood",
      title: "Mood and distress",
      status: therm == null ? "no-data" : therm >= 7 || (phq ?? 0) >= 3 ? "attention" : therm >= 4 ? "watch" : "ok",
      value: therm == null ? "Not logged" : `Distress ${therm.toFixed(1)}/10`,
      plain:
        therm == null
          ? "Distress screening takes under two minutes and changes what the other three numbers mean."
          : therm >= 4
            ? "Distress at this level independently worsens fatigue and concentration; treating it usually improves both."
            : "Distress is in a manageable range.",
      toolLink: "/institute/distress",
    },
    {
      key: "function",
      title: "Function and work",
      status: cognition.length === 0 && activity.length === 0 ? "no-data" : workHits >= 3 ? "attention" : workHits >= 1 ? "watch" : "ok",
      value: cognition.length === 0 ? "Not logged" : `${workHits} day(s) with work, school, or driving impact`,
      plain:
        workHits >= 1
          ? "Functional impact is the part clinicians act on fastest, and it is what supports an accommodation request."
          : "No functional impact recorded in your recent entries.",
      toolLink: "/institute/cognition",
    },
  ];

  const loggedCount = [sleep, activity, cognition, distress].filter((a) => a.length > 0).length;

  // --- Interactions -------------------------------------------------------
  const interactions: Interaction[] = [];
  if ((sleepHours ?? 8) < 7 && (cogAvg ?? 0) >= 3) {
    interactions.push({
      title: "Short sleep is sitting underneath your cognitive symptoms",
      detail:
        "Sleep restriction produces attention and working-memory problems that look identical to treatment-related cognitive change. Treating sleep first often reduces the cognitive complaint substantially.",
      action: "Ask for a sleep evaluation before, or alongside, neurocognitive testing.",
    });
  }
  if ((ess ?? 0) >= 11) {
    interactions.push({
      title: "Daytime sleepiness in the range where sleep apnea is common",
      detail:
        "Survivors carry elevated rates of sleep-disordered breathing, particularly after head and neck treatment, weight change, or hypothyroidism. It is frequently missed and highly treatable.",
      action: "Request a sleep study rather than another fatigue workup on its own.",
    });
  }
  if ((therm ?? 0) >= 4 && (fatigueAfter ?? 0) >= 4) {
    interactions.push({
      title: "Distress and fatigue are reinforcing each other",
      detail:
        "Mood and fatigue share a bidirectional relationship in survivorship. Addressing only one usually produces a partial and short-lived improvement.",
      action: "Ask for a survivorship-informed behavioral health referral alongside the fatigue workup.",
    });
  }
  if (weekMinutes < 75 && (fatigueAfter ?? 0) >= 4) {
    interactions.push({
      title: "Low activity with high fatigue is the deconditioning loop",
      detail:
        "Fatigue reduces activity, and reduced activity worsens fatigue. Graded exercise is the single intervention with the strongest evidence for cancer-related fatigue.",
      action: "Ask about supervised or oncology rehabilitation rather than being told to rest.",
    });
  }
  if (workHits >= 1 && (cogAvg ?? 0) >= 3) {
    interactions.push({
      title: "Cognitive symptoms are reaching your work or driving",
      detail:
        "Documented functional impact is what unlocks accommodations and what most clinicians act on. Your logs are the documentation.",
      action: "Bring the printed log to the visit and ask directly about written accommodations.",
    });
  }
  if (interactions.length === 0 && loggedCount > 0) {
    interactions.push({
      title: "No strong interaction detected yet",
      detail: "Your domains are not currently pulling on each other in the pattern we look for.",
      action: "Keep logging weekly; the cluster view becomes more informative with two to three weeks of entries.",
    });
  }

  const functionalImpact =
    workHits >= 3
      ? "Your entries show repeated impact on work, school, or driving. That is a clinical finding, not a personal failing, and it belongs in the visit note."
      : workHits >= 1
        ? "There is some impact on work, school, or driving. Track it so the pattern is visible rather than anecdotal."
        : "No functional impact recorded yet. If that changes, note the day it happens rather than reconstructing it later.";

  const escalation = [
    "New or rapidly worsening confusion, weakness on one side, or trouble speaking: seek urgent care.",
    "Thoughts of harming yourself: contact the 988 Suicide and Crisis Lifeline immediately.",
    "Fatigue with breathlessness, chest discomfort, or swelling: this needs a cardiac evaluation, not reassurance.",
    "Fatigue with fever, bruising, or unexplained weight loss: contact oncology the same week.",
  ];

  const clinicianNotes = [
    "Screen the cluster, not the presenting symptom. More than three quarters of survivors carry at least one symptom and a substantial share carry several concurrently.",
    "Rule out reversible contributors before attributing to treatment: anemia, hypothyroidism, sleep-disordered breathing, depression, deconditioning, medication effects, hypogonadism.",
    "Anthracycline or chest radiation exposure changes the meaning of fatigue and exercise intolerance. Consider echocardiography before a deconditioning diagnosis.",
    "Functional consequence drives the plan: work, driving, caregiving, and independence, with accommodation letters where indicated.",
    "Graded exercise plus cognitive rehabilitation has better evidence than pharmacologic management for the cognitive component.",
  ];

  return { domains, loggedCount, interactions, functionalImpact, escalation, clinicianNotes };
}
